import { useAuth } from '@/auth/useAuth';
import { NtcResumenTable } from '@/components/asesorias/NtcResumenTable';
import { LoadingContainer, Modal, Textarea } from '@/components/ui';
import { ProgressIndicators } from '@/config/app.config';
import { useAudioProcessingStatus } from '@/hooks';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { useAsesoriaById } from '@/hooks/useAsesorias';
import { useResumen } from '@/hooks/useResumen';
import '@/pages/admin/admin.css';
import { asesoriasService, type SimularFinalizarAsesoriaResponse } from '@/services/asesorias.service';
import { dashboardService } from '@/services/dashboard.service';
import { reunionesService } from '@/services/reuniones.service';
import type { Reunion } from '@/types/types';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { elementosCorregiblesPendientes, type RevisionEntry } from '@/utils/revisionHistorial';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Circle, Music } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

function formatDate(dateStr?: string): string {
  if (!dateStr) {
    return 'N/A';
  }
  try {
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}

function formatAudioDate(date: Date | string): string {
  if (typeof date === 'string') {
    return formatDate(date);
  } else {
    return formatDate(date.toISOString());
  }
}

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h === 0 && m === 0) return `${s}s`;
  if (h === 0) return `${m}m ${s}s`;
  return `${h}h ${m}m ${s}s`;
}

function formatHours(decimalHours: number): string {
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
}

function getProgFillColor(porcentaje: number): string {
  if (porcentaje >= ProgressIndicators.excellent) return '#2d7a4f';
  if (porcentaje >= ProgressIndicators.good) return '#b56b00';
  return '#c0392b';
}

// Las reuniones se muestran en hora de Colombia (GMT-5, offset fijo sin horario de verano).
const BOGOTA_TZ = 'America/Bogota';

function formatReunionRango(r: Reunion): string {
  if (!r.fechaInicio) return 'Sin fecha';
  const ini = new Date(r.fechaInicio);
  const fecha = ini.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: BOGOTA_TZ });
  const horaIni = ini.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: BOGOTA_TZ });
  const horaFin = r.fechaFin
    ? new Date(r.fechaFin).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: BOGOTA_TZ })
    : '';
  return horaFin ? `${fecha} · ${horaIni} – ${horaFin}` : `${fecha} · ${horaIni}`;
}

const REUNION_ESTADO: Record<Reunion['estado'], { label: string; bg: string; color: string }> = {
  PROGRAMADA: { label: 'Programada', bg: '#eaf1fb', color: '#1e5fa8' },
  COMPLETADA: { label: 'Completada', bg: '#eaf5ee', color: '#2d7a4f' },
  CANCELADA: { label: 'Cancelada', bg: '#fdf0ee', color: '#c0392b' },
};

export function FinalizarAsesoriaPage() {
  const { user } = useAuth();
  const { nit, asesoriaId } = useParams<{ nit: string; asesoriaId: string }>();
  const navigate = useNavigate();
  const [expandResumen, setExpandResumen] = useState(false);
  const [conclusiones, setConclusiones] = useState('');
  const [compromisos, setCompromisos] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingAudioStatus, setIsCheckingAudioStatus] = useState(false);
  const [showAudioProcessingWarning, setShowAudioProcessingWarning] = useState(false);
  const [direccion, setDireccion] = useState('');

  const initialFormValuesRef = useRef({
    conclusiones: '',
    compromisos: '',
    direccion: '',
  } as { conclusiones: string; compromisos: string; direccion: string | null });

  const [modal, setModal] = useState<{
    isOpen: boolean;
    isSuccess: boolean;
    message: string;
    items?: Array<string | { text: string; url?: string }>;
  }>({
    isOpen: false,
    isSuccess: false,
    message: '',
  });

  const { data, isLoading, error } = useResumen(nit ?? '', typeof asesoriaId === 'string' ? Number(asesoriaId) : asesoriaId);
  const { data: asesoriaDetalle, refetch: refetchAsesoria } = useAsesoriaById(nit ?? '', asesoriaId);

  const [selectedReunion, setSelectedReunion] = useState<Reunion | null>(null);
  const [isReunionSubmitting, setIsReunionSubmitting] = useState(false);
  const [reunionError, setReunionError] = useState<string | null>(null);

  const handleActionReunion = async (action: 'COMPLETAR' | 'CANCELAR') => {
    if (!selectedReunion || !nit) return;
    setIsReunionSubmitting(true);
    setReunionError(null);
    try {
      if (action === 'COMPLETAR') {
        await reunionesService.completarDeAsesoriaActual(selectedReunion.reunionId, nit);
      } else {
        await reunionesService.cancelarDeAsesoriaActual(selectedReunion.reunionId, nit);
      }
      await refetchAsesoria();
      setSelectedReunion(null);
    } catch (err: any) {
      setReunionError(err?.message || 'Error al gestionar la reunión');
    } finally {
      setIsReunionSubmitting(false);
    }
  };
  const {
    data: audioProcessingStatus,
    isLoading: isAudioProcessingStatusLoading,
    isRefreshing: isAudioProcessingStatusRefreshing,
    error: audioProcessingStatusError,
    isNotFound: isAudioProcessingStatusNotFound,
    refetch: refetchAudioProcessingStatus,
  } = useAudioProcessingStatus(nit ?? '', Boolean(nit));

  const hasDireccion = data?.data?.empresa?.direccion && data.data.empresa.direccion.trim() !== '';

  const hasUnsavedChanges = useMemo(() => {
    const normalize = (value: string | null | undefined) => (value ?? '').trim();
    return (
      normalize(conclusiones) !== normalize(initialFormValuesRef.current.conclusiones) ||
      normalize(compromisos) !== normalize(initialFormValuesRef.current.compromisos) ||
      (!hasDireccion && normalize(direccion) !== normalize(initialFormValuesRef.current.direccion))
    );
  }, [conclusiones, compromisos, direccion, hasDireccion]);

  const confirmDiscardChanges = (): boolean => {
    if (!hasUnsavedChanges) return true;
    return window.confirm('Hay cambios sin guardar. Si sales ahora, se perderán los datos ingresados. ¿Deseas continuar?');
  };

  useEffect(() => {
    if (data?.data?.empresa.direccion && hasDireccion) {
      setDireccion(data.data.empresa.direccion);
      initialFormValuesRef.current.direccion = data.data.empresa.direccion;
    }
  }, [data, hasDireccion]);

  // Pre-cargar compromisos/conclusiones si ya existen en la asesoría
  useEffect(() => {
    if (!asesoriaDetalle) return;
    if (asesoriaDetalle.compromisos) {
      setCompromisos(asesoriaDetalle.compromisos);
      initialFormValuesRef.current.compromisos = asesoriaDetalle.compromisos;
    }
    if (asesoriaDetalle.conclusiones) {
      setConclusiones(asesoriaDetalle.conclusiones);
      initialFormValuesRef.current.conclusiones = asesoriaDetalle.conclusiones;
    }
  }, [asesoriaDetalle]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && !isSubmitting) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isSubmitting]);

  // ── Gate de correcciones: no se puede enviar mientras haya elementos rechazados sin resolver ──
  // (Estos hooks deben ir ANTES de cualquier return temprano.)
  const [revisionHistorial, setRevisionHistorial] = useState<RevisionEntry[]>([]);
  useEffect(() => {
    if (!nit || !asesoriaId) return;
    dashboardService
      .getRevisionHistorial(nit, Number(asesoriaId))
      .then((res) => {
        if (res.success) setRevisionHistorial(res.historial as unknown as RevisionEntry[]);
      })
      .catch(() => {});
  }, [nit, asesoriaId]);

  const cadenasEvidencias = useMemo(() => {
    const evs: any[] = (asesoriaDetalle?.evidencias ?? (data?.data?.asesoria as any)?.evidencias) || [];
    return agruparCadenaArchivos(
      evs,
      (e) => e.evidenciaId,
      (e) => e.reemplazaEvidenciaId ?? null
    );
  }, [asesoriaDetalle?.evidencias, data?.data?.asesoria]);

  const cadenasAudios = useMemo(() => {
    const aus: any[] = (data?.data?.audios ?? asesoriaDetalle?.audios) || [];
    return agruparCadenaArchivos(
      aus,
      (a) => a.audioId,
      (a) => a.reemplazaAudioId ?? null
    );
  }, [data?.data?.audios, asesoriaDetalle?.audios]);

  // Un elemento (requisito, terna, evidencia o audio) sigue pendiente mientras `estaResuelto`
  // sea false — el mismo predicado compartido que usa el backend, que ya cuenta tanto un
  // re-upload (CORREGIDO) como una simple respuesta al administrador (RESPUESTA_ASESOR) como
  // formas válidas de resolución.
  const elementosSinResolver = useMemo(
    () =>
      elementosCorregiblesPendientes(revisionHistorial, {
        evidencias: cadenasEvidencias as unknown as Array<Record<string, unknown>>,
        audios: cadenasAudios as unknown as Array<Record<string, unknown>>,
      }),
    [revisionHistorial, cadenasEvidencias, cadenasAudios]
  );

  const showSecretPanel = useAdminSecretPanel();
  const [simulacionModal, setSimulacionModal] = useState<{
    isOpen: boolean;
    isSimulando: boolean;
    resultado: (SimularFinalizarAsesoriaResponse & { items?: Array<string | { text: string; url?: string }> }) | null;
  }>({
    isOpen: false,
    isSimulando: false,
    resultado: null,
  });

  if (isLoading) {
    return <LoadingContainer text="Cargando datos..." />;
  }

  if (error || !data?.success || !data.data) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">{error || 'Asesoría no encontrada'}</h2>
        <Link to="/" className="admin-btn primary mt-4 inline-flex">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const { empresa, resumenAntes, resumenDespues, asesoria, audios } = data.data;
  const audioItems = asesoriaDetalle?.audios ?? audios ?? [];
  const audioTotalSeconds = audioItems.reduce((s, a) => s + (a.duracionSeg ?? a.duracion ?? 0), 0);
  // `horas` sigue en 0 mientras el asesor no la registra explícitamente — si ya hay audios
  // grabados, se usa su duración acumulada como estimado (mismo criterio que el resto de la app:
  // AsesoriasPanel, HorasResumenCard, AsesoriaHistorialTable). Sin este fallback, `sinHoras` más
  // abajo bloqueaba el envío a revisión aunque ya hubiera trabajo real invertido y grabado.
  const asesoriaHorasReales = asesoriaDetalle?.horas ?? asesoria.horas ?? 0;
  const asesoriaHoras = asesoriaHorasReales > 0 ? asesoriaHorasReales : audioTotalSeconds / 3600;

  const esRequiereCorrecciones = (asesoriaDetalle?.estado ?? (asesoria as any)?.estado) === 'REQUIERE_CORRECCIONES';
  const hayPendientesCorreccion = elementosSinResolver.length > 0;

  // Reuniones de la asesoría y validaciones para habilitar el envío a revisión.
  const reuniones = asesoriaDetalle?.reuniones ?? [];
  const reunionesSinCompletar = reuniones.filter((r) => r.estado === 'PROGRAMADA');
  const hayReunionesSinCompletar = reunionesSinCompletar.length > 0;
  const faltanConclusiones = conclusiones.trim() === '';
  const faltanCompromisos = compromisos.trim() === '';
  const currentAsesorId = user?.asesor?.id ? Number(user.asesor.id) : null;
  const originalAsesorId =
    asesoriaDetalle?.asesor?.asesorId ??
    (asesoria as any)?.asesor?.asesorId ??
    (asesoria as any)?.asesorId ??
    (data?.data?.asesoria as any)?.asesorId;

  const esOriginalAsesor =
    currentAsesorId !== null && originalAsesorId !== undefined && originalAsesorId !== null
      ? currentAsesorId === Number(originalAsesorId)
      : true;

  const sinHoras = esOriginalAsesor && asesoriaHoras <= 0;

  const motivoBloqueo = sinHoras
    ? 'No se puede enviar a revisión una asesoría sin horas registradas'
    : hayPendientesCorreccion
      ? 'Resuelve los elementos rechazados antes de enviar'
      : faltanConclusiones || faltanCompromisos
        ? 'Completa las conclusiones y los compromisos antes de enviar'
        : hayReunionesSinCompletar
          ? 'Completa o cancela las reuniones pendientes antes de enviar'
          : undefined;

  const handleSimularFinalizar = async () => {
    if (!nit || !asesoriaId) return;
    setSimulacionModal({ isOpen: true, isSimulando: true, resultado: null });

    const faltantes: Array<string | { text: string; url?: string }> = [];
    if (sinHoras) faltantes.push('Registra las horas de la asesoría (Horas = 0).');
    if (esRequiereCorrecciones && hayPendientesCorreccion) {
      for (const el of elementosSinResolver) {
        let label = `Elemento rechazado (${el.elementoTipo})`;
        let subtab = 'matriz';
        if (el.elementoTipo === 'EVIDENCIA') {
          subtab = 'evidencias';
          const ev = (asesoriaDetalle?.evidencias ?? (data?.data?.asesoria as any)?.evidencias ?? []).find(
            (e: any) => String(e.evidenciaId) === String(el.elementoId)
          );
          label = ev ? `Evidencia: ${ev.nombre}` : `Evidencia ID #${el.elementoId}`;
        } else if (el.elementoTipo === 'AUDIO') {
          subtab = 'audios';
          const au = (data?.data?.audios ?? asesoriaDetalle?.audios ?? []).find((a: any) => String(a.audioId) === String(el.elementoId));
          label = au ? `Audio: ${(au as any).nombre ?? 'Grabación'}` : `Audio ID #${el.elementoId}`;
        } else if (el.elementoTipo === 'REQUISITO') {
          subtab = 'matriz';
          label = `Matriz: Requisito`;
        } else if (el.elementoTipo === 'PLANMEJORA') {
          subtab = 'matriz';
          label = `Matriz: Plan de Mejora`;
        } else if (el.elementoTipo === 'OBSERVACIONES') {
          subtab = 'matriz';
          label = `Matriz: Observaciones`;
        } else if (el.elementoTipo === 'FECHAIMPLEMENTACION') {
          subtab = 'matriz';
          label = `Matriz: Fecha de Implementación`;
        }

        const url = `/empresa/${nit}/asesoria/${asesoriaId}?tab=correcciones&subtab=${subtab}&item=${el.elementoId}`;
        faltantes.push({ text: `${label} — sin resolver`, url });
      }
    }
    if (faltanConclusiones) faltantes.push('Completa las conclusiones.');
    if (faltanCompromisos) faltantes.push('Completa los compromisos.');
    if (hayReunionesSinCompletar) faltantes.push(`Completa o cancela las ${reunionesSinCompletar.length} reunión(es) sin completar.`);

    try {
      const direccionFinal = hasDireccion ? empresa.direccion : direccion;
      const resp = await asesoriasService.simularFinalizar(
        nit,
        Number(asesoriaId),
        compromisos.trim(),
        conclusiones.trim(),
        typeof direccionFinal === 'string' ? direccionFinal.trim() : direccionFinal,
        new Date().toISOString()
      );

      const backendOk = Boolean(resp.ok && resp.success);
      const localOk = faltantes.length === 0;
      const isOk = backendOk && localOk;

      setSimulacionModal({
        isOpen: true,
        isSimulando: false,
        resultado: {
          ...resp,
          ok: isOk,
          success: isOk,
          message: isOk
            ? resp.message || 'Simulación exitosa: La asesoría se enviaría a revisión correctamente.'
            : !backendOk
              ? resp.message || 'El servidor indicó que el envío no puede realizarse.'
              : 'La asesoría NO se puede enviar a revisión por faltantes en el formulario:',
          items: !isOk ? (faltantes.length > 0 ? faltantes : undefined) : undefined,
        },
      });
    } catch (err: any) {
      const isOk = faltantes.length === 0;
      setSimulacionModal({
        isOpen: true,
        isSimulando: false,
        resultado: {
          simulacion: true,
          ok: isOk,
          success: isOk,
          message: isOk
            ? 'Simulación local exitosa: La asesoría cumple todos los requisitos para ser enviada.'
            : err?.message || 'Error durante la simulación de envío',
          items: faltantes.length > 0 ? faltantes : undefined,
        },
      });
    }
  };

  const checklist = [
    { label: 'Horas de asesoría registradas', ok: !sinHoras },
    { label: 'Conclusiones completas', ok: !faltanConclusiones },
    { label: 'Compromisos completos', ok: !faltanCompromisos },
    { label: 'Sin reuniones en curso', ok: !hayReunionesSinCompletar },
    ...(esRequiereCorrecciones ? [{ label: 'Correcciones resueltas o apeladas', ok: !hayPendientesCorreccion }] : []),
  ];

  const enviarAsesoria = async () => {
    if (!confirm('¿Estas seguro? La asesoria sera enviada al administrador para revision.')) {
      return;
    }

    if (!nit) {
      setModal({ isOpen: true, isSuccess: false, message: 'Datos incompletos' });
      return;
    }
    if (sinHoras) {
      setModal({
        isOpen: true,
        isSuccess: false,
        message: 'No se puede enviar a revisión una asesoria sin horas registradas, si crees que es un error actualiza la pagina',
      });
      return;
    }
    if (hayPendientesCorreccion) {
      setModal({
        isOpen: true,
        isSuccess: false,
        message: `Quedan ${elementosSinResolver.length} elemento(s) rechazado(s) sin resolver. Corrígelos (re-súbelos o edítalos) o responde al administrador antes de volver a enviar.`,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const compromisosLimpios = compromisos.trim();
      const conclusionesLimpias = conclusiones.trim();
      const direccionFinal = hasDireccion ? empresa.direccion : direccion;
      const direccionLimpia = typeof direccionFinal === 'string' ? direccionFinal.trim() : direccionFinal;

      const response = await asesoriasService.finalizar(
        nit,
        Number(asesoriaId),
        compromisosLimpios,
        conclusionesLimpias,
        direccionLimpia,
        new Date().toISOString()
      );

      if (response.success) {
        initialFormValuesRef.current = {
          conclusiones: conclusionesLimpias,
          compromisos: compromisosLimpios,
          direccion: direccionLimpia,
        };
      }

      setModal({
        isOpen: true,
        isSuccess: response.success,
        message: response.message || 'Asesoria enviada a revisión. Esperando aprobacion del administrador.',
      });
    } catch (err) {
      setModal({
        isOpen: true,
        isSuccess: false,
        message: err instanceof Error ? err.message : 'Error al enviar a revisión',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalizar = async () => {
    if (!nit) {
      setModal({ isOpen: true, isSuccess: false, message: 'Datos incompletos' });
      return;
    }

    // Se reúnen TODOS los faltantes para mostrarlos juntos en el modal.
    const faltantes: Array<string | { text: string; url?: string }> = [];
    if (sinHoras) faltantes.push('Registra las horas de la asesoría.');
    if (esRequiereCorrecciones && hayPendientesCorreccion) {
      for (const el of elementosSinResolver) {
        let label = `Elemento rechazado (${el.elementoTipo})`;
        let subtab = 'matriz';
        if (el.elementoTipo === 'EVIDENCIA') {
          subtab = 'evidencias';
          const ev = (asesoriaDetalle?.evidencias ?? (data?.data?.asesoria as any)?.evidencias ?? []).find(
            (e: any) => String(e.evidenciaId) === String(el.elementoId)
          );
          label = ev ? `Evidencia: ${ev.nombre}` : `Evidencia ID #${el.elementoId}`;
        } else if (el.elementoTipo === 'AUDIO') {
          subtab = 'audios';
          const au = (data?.data?.audios ?? asesoriaDetalle?.audios ?? []).find((a: any) => String(a.audioId) === String(el.elementoId));
          label = au ? `Audio: ${(au as any).nombre ?? 'Grabación'}` : `Audio ID #${el.elementoId}`;
        } else if (el.elementoTipo === 'REQUISITO') {
          subtab = 'matriz';
          label = `Matriz: Requisito (ID: ${el.elementoId})`;
        } else if (el.elementoTipo === 'PLANMEJORA') {
          subtab = 'matriz';
          label = `Matriz: Plan de Mejora`;
        } else if (el.elementoTipo === 'OBSERVACIONES') {
          subtab = 'matriz';
          label = `Matriz: Observaciones`;
        } else if (el.elementoTipo === 'FECHAIMPLEMENTACION') {
          subtab = 'matriz';
          label = `Matriz: Fecha de Implementación`;
        }

        const url = `/empresa/${nit}/asesoria/${asesoriaId}?tab=correcciones&subtab=${subtab}&item=${el.elementoId}`;
        faltantes.push({ text: `${label} — sin resolver`, url });
      }
    }
    if (faltanConclusiones) faltantes.push('Completa las conclusiones.');
    if (faltanCompromisos) faltantes.push('Completa los compromisos.');
    if (hayReunionesSinCompletar) faltantes.push(`Completa o cancela las ${reunionesSinCompletar.length} reunión(es) sin completar.`);

    if (faltantes.length > 0) {
      setModal({
        isOpen: true,
        isSuccess: false,
        message: 'Para enviar a revisión primero debes resolver lo siguiente:',
        items: faltantes,
      });
      return;
    }

    try {
      setIsCheckingAudioStatus(true);
      const latestStatus = await refetchAudioProcessingStatus();

      if (latestStatus && latestStatus.hasAudiosEnProcesamiento) {
        setShowAudioProcessingWarning(true);
        return;
      }

      await enviarAsesoria();
    } catch {
      await enviarAsesoria();
    } finally {
      setIsCheckingAudioStatus(false);
    }
  };

  const mejora = resumenDespues.cumplimiento - resumenAntes.cumplimiento;

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Modal — resultado de finalizar */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => {
          setModal((prev) => ({ ...prev, isOpen: false }));
          if (modal.isSuccess) navigate(`/empresa/${nit}`);
        }}
        title={modal.isSuccess ? 'Asesoría enviada a revisión' : 'No se puede enviar a revisión'}
      >
        <div className="space-y-4">
          {modal.isSuccess ? (
            <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#eaf5ee', border: '1px solid #b7dfc6' }}>
              <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#2d7a4f' }} fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium text-[13px]" style={{ color: '#2d7a4f' }}>
                  Asesoría enviada a revisión
                </p>
                <p className="text-[12px] mt-1" style={{ color: '#2d7a4f' }}>
                  La asesoría ha sido enviada al administrador para su revisión. Recibirás una notificación cuando sea aprobada.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#fdf0ee', border: '1px solid #f0b9b3' }}>
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#c0392b' }} />
              <div>
                <p className="text-[13px]" style={{ color: '#c0392b' }}>
                  {modal.message}
                </p>
                {modal.items && modal.items.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {modal.items.map((item, idx) => {
                      const isObj = typeof item === 'object' && item !== null && 'text' in item;
                      const text = isObj ? (item as any).text : String(item);
                      const url = isObj ? (item as any).url : undefined;
                      return (
                        <li key={idx} className="flex items-start gap-1.5 text-[12px]" style={{ color: '#c0392b' }}>
                          <span className="shrink-0">•</span>
                          {url ? (
                            <Link
                              to={url}
                              onClick={() => setModal((prev) => ({ ...prev, isOpen: false }))}
                              className="underline hover:opacity-80 font-medium cursor-pointer"
                              style={{ color: '#c0392b' }}
                            >
                              {text}
                            </Link>
                          ) : (
                            <span>{text}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button
            className="admin-btn primary"
            onClick={() => {
              setModal((prev) => ({ ...prev, isOpen: false }));
              if (modal.isSuccess) navigate(`/empresa/${nit}`);
            }}
          >
            {modal.isSuccess ? 'Cerrar' : 'Entendido'}
          </button>
        </div>
      </Modal>

      {/* Modal — gestionar reunión */}
      <Modal
        isOpen={selectedReunion !== null}
        onClose={() => {
          setSelectedReunion(null);
          setReunionError(null);
        }}
        title="Gestionar Reunión"
        size="sm"
      >
        {selectedReunion && (
          <div className="space-y-4">
            <div className="bg-[#fcfbf9] border border-[#f0ece8] rounded-xl p-4 text-[13px] text-[#1a1a1a] space-y-2">
              <p>
                <strong>Fecha/Hora:</strong> {formatReunionRango(selectedReunion)}
              </p>
              {selectedReunion.motivo && (
                <p>
                  <strong>Motivo:</strong> {selectedReunion.motivo}
                </p>
              )}
            </div>
            {reunionError && <p className="text-[12px] font-medium text-[#c0392b]">{reunionError}</p>}
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                className="admin-btn"
                onClick={() => {
                  setSelectedReunion(null);
                  setReunionError(null);
                }}
                disabled={isReunionSubmitting}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-[12px] font-medium text-white transition-colors"
                style={{ background: '#c0392b' }}
                onClick={() => handleActionReunion('CANCELAR')}
                disabled={isReunionSubmitting}
              >
                {isReunionSubmitting ? 'Procesando...' : 'Cancelar (Denegar)'}
              </button>
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-[12px] font-medium text-white transition-colors"
                style={{ background: '#2d7a4f' }}
                onClick={() => handleActionReunion('COMPLETAR')}
                disabled={isReunionSubmitting}
              >
                {isReunionSubmitting ? 'Procesando...' : 'Completar (Aceptar)'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal — audios en procesamiento */}
      <Modal
        isOpen={showAudioProcessingWarning}
        onClose={() => setShowAudioProcessingWarning(false)}
        title="Audios en procesamiento"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#fdf3e0', border: '1px solid #f0dba0' }}>
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#b56b00' }} />
            <div>
              <p className="font-medium text-[13px]" style={{ color: '#b56b00' }}>
                Se detectaron audios en procesamiento
              </p>
              <p className="text-[12px] mt-1" style={{ color: '#b56b00' }}>
                Hay {audioProcessingStatus?.totalJobsEnProcesamiento ?? 0} job(s) de audio en estado pendiente o en progreso. Puedes esperar
                a que finalicen o continuar de todos modos.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button className="admin-btn" onClick={() => setShowAudioProcessingWarning(false)} disabled={isSubmitting}>
              Esperar
            </button>
            <button
              className="rounded-lg px-4 py-2 text-[12px] font-medium text-white"
              style={{ background: '#c0392b' }}
              onClick={async () => {
                setShowAudioProcessingWarning(false);
                await enviarAsesoria();
              }}
              disabled={isSubmitting}
            >
              Continuar y enviar
            </button>
          </div>
        </div>
      </Modal>

      <div className="max-w-3xl mx-auto px-6 py-6">
        {/* Back button */}
        <button
          onClick={() => {
            if (confirmDiscardChanges()) navigate(`/empresa/${nit}/asesoria/${asesoriaId}`);
          }}
          className="flex items-center gap-1.5 text-[12px] text-[#999] hover:text-[#1a1a1a] mb-4 transition-colors"
        >
          ← Volver a asesoría
        </button>

        {/* Header */}
        <div className="admin-detail-panel flex items-start justify-between gap-4" style={{ marginBottom: 20 }}>
          <div>
            <p className="text-[16px] font-semibold" style={{ color: '#1a1a1a' }}>
              Enviar a Verificación
            </p>
            <p className="text-[12px] mt-0.5" style={{ color: '#999' }}>
              {empresa.nombre}
            </p>
          </div>
          <span className="admin-chip" style={{ background: '#fdf3e0', color: '#b56b00' }}>
            <AlertTriangle className="w-3 h-3" />
            La asesoría no podrá editarse
          </span>
        </div>

        {/* Comparativa Antes/Después — colapsable */}
        <div className="admin-card">
          <button
            type="button"
            onClick={() => setExpandResumen(!expandResumen)}
            className="admin-card-header w-full"
            style={{ cursor: 'pointer', background: 'none', border: 'none', justifyContent: 'space-between' }}
          >
            <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
              Comparativa Antes vs Después
            </span>
            {expandResumen ? (
              <ChevronUp className="w-4 h-4" style={{ color: '#999' }} />
            ) : (
              <ChevronDown className="w-4 h-4" style={{ color: '#999' }} />
            )}
          </button>

          {expandResumen && (
            <div className="px-5 py-4 space-y-5">
              {/* Por NTC */}
              {asesoriaDetalle?.resumenPorNtc && asesoriaDetalle.resumenPorNtc.length > 0 && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wide mb-3" style={{ color: '#999' }}>
                    Detalle por norma técnica
                  </p>
                  <NtcResumenTable rows={asesoriaDetalle.resumenPorNtc} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Datos de Asesoría */}
        <div className="admin-detail-panel">
          <p className="text-[13px] font-semibold mb-3" style={{ color: '#1a1a1a' }}>
            Datos de Asesoría
          </p>
          <div className="admin-detail-grid" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 0 }}>
            <div className="admin-detail-field">
              <p className="text-[11px] mb-1" style={{ color: '#999' }}>
                Fecha Inicio
              </p>
              <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                {formatDate(asesoria.fechaInicio)}
              </p>
            </div>
            <div className="admin-detail-field">
              <p className="text-[11px] mb-1" style={{ color: '#999' }}>
                Horas invertidas
              </p>
              <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                {formatHours(asesoriaHoras)}
              </p>
            </div>
            <div className="admin-detail-field" style={{ gridColumn: hasDireccion ? '1 / -1' : undefined }}>
              <p className="text-[11px] mb-1" style={{ color: '#999' }}>
                Objetivo
              </p>
              <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                {asesoria.objetivo}
              </p>
            </div>
            {hasDireccion && (
              <div className="admin-detail-field">
                <p className="text-[11px] mb-1" style={{ color: '#999' }}>
                  Dirección
                </p>
                <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                  {empresa.direccion}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tabla Audios */}
        <div className="admin-card">
          <div className="admin-card-header" style={{ justifyContent: 'space-between' }}>
            <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
              Grabaciones
            </span>
            {audioItems.length > 0 && (
              <span className="text-[11px]" style={{ color: '#999' }}>
                {audioItems.length} grabación{audioItems.length !== 1 ? 'es' : ''} · {formatDuration(audioTotalSeconds)} total
              </span>
            )}
          </div>
          {audioItems.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <Music className="w-8 h-8 mx-auto mb-2" style={{ color: '#d4d0cb' }} />
              <p className="text-[13px]" style={{ color: '#999' }}>
                No hay grabaciones registradas
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#f0ece8]">
              {audioItems.map((audio, idx) => {
                const audioKey = audio.audioId?.toString() ?? audio.googleId ?? String(idx);
                const durSeg = audio.duracionSeg ?? audio.duracion ?? 0;
                const audioLink = `https://drive.google.com/file/d/${audio.googleId}/preview`;
                return (
                  <div key={audioKey} className="flex items-center gap-3 px-5 py-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#f0ece8' }}>
                      <Music className="w-4 h-4" style={{ color: '#8a7e74' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                        {formatAudioDate(audio.fechaGrabacion)}
                      </p>
                      <p className="text-[11px]" style={{ color: '#999' }}>
                        {durSeg > 0 ? formatDuration(durSeg) : 'Duración no disponible'}
                      </p>
                    </div>
                    <a
                      href={audioLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] font-medium shrink-0"
                      style={{ color: '#1e5fa8' }}
                    >
                      Abrir →
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Estado procesamiento de audios */}
        <div className="admin-card">
          <div className="admin-card-header" style={{ justifyContent: 'space-between' }}>
            <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
              Estado de procesamiento de audios
            </span>
            <button
              className="admin-btn"
              style={{ padding: '4px 10px', fontSize: '11px' }}
              onClick={() => void refetchAudioProcessingStatus()}
              disabled={isAudioProcessingStatusRefreshing}
            >
              {isAudioProcessingStatusRefreshing ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
          <div className="px-5 py-4">
            {isAudioProcessingStatusLoading ? (
              <span className="admin-chip" style={{ background: '#f2f1ee', color: '#666' }}>
                Consultando estado...
              </span>
            ) : isAudioProcessingStatusNotFound ? (
              <span className="admin-chip" style={{ background: '#fdf0ee', color: '#c0392b' }}>
                No existe asesoría activa para consultar estado
              </span>
            ) : audioProcessingStatusError ? (
              <span className="admin-chip" style={{ background: '#fdf3e0', color: '#b56b00' }}>
                {audioProcessingStatusError}
              </span>
            ) : audioProcessingStatus ? (
              <span
                className="admin-chip"
                style={
                  audioProcessingStatus.hasAudiosEnProcesamiento
                    ? { background: '#fdf3e0', color: '#b56b00' }
                    : { background: '#eaf5ee', color: '#2d7a4f' }
                }
              >
                {audioProcessingStatus.hasAudiosEnProcesamiento ? 'Hay audios en procesamiento' : 'Sin audios en procesamiento'}
              </span>
            ) : (
              <span className="admin-chip" style={{ background: '#f2f1ee', color: '#999' }}>
                Sin datos por el momento
              </span>
            )}
          </div>
        </div>

        {/* Reuniones de la asesoría */}
        {reuniones.length > 0 && (
          <div className="admin-card">
            <div className="admin-card-header" style={{ justifyContent: 'space-between' }}>
              <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
                Reuniones
              </span>
              <span className="text-[11px]" style={{ color: '#999' }}>
                {reuniones.length} reunión{reuniones.length !== 1 ? 'es' : ''}
              </span>
            </div>
            <div className="divide-y divide-[#f0ece8]">
              {reuniones.map((r) => (
                <div key={r.reunionId} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium" style={{ color: '#1a1a1a' }}>
                      {formatReunionRango(r)}
                    </p>
                    {r.motivo && (
                      <p className="text-[11px] truncate" style={{ color: '#999' }}>
                        {r.motivo}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="admin-chip" style={{ background: REUNION_ESTADO[r.estado].bg, color: REUNION_ESTADO[r.estado].color }}>
                      {REUNION_ESTADO[r.estado].label}
                    </span>
                    {r.estado === 'PROGRAMADA' && (
                      <button
                        type="button"
                        onClick={() => setSelectedReunion(r)}
                        className="admin-btn"
                        style={{ padding: '4px 10px', fontSize: '11px', minHeight: 'unset', height: 'unset' }}
                      >
                        Gestionar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conclusiones / Compromisos / Dirección */}
        <div className="admin-card">
          <div className="admin-card-header">
            <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
              Conclusiones y Compromisos
            </span>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div>
              <p className="text-[11px] font-medium mb-1.5" style={{ color: '#999' }}>
                CONCLUSIONES <span style={{ color: '#c0392b' }}>*</span>
              </p>
              <Textarea
                label=""
                placeholder="Resumen de los logros alcanzados, hallazgos principales y recomendaciones finales..."
                rows={4}
                value={conclusiones}
                onChange={(e) => setConclusiones(e.target.value)}
              />
            </div>
            <div>
              <p className="text-[11px] font-medium mb-1.5" style={{ color: '#999' }}>
                COMPROMISOS <span style={{ color: '#c0392b' }}>*</span>
              </p>
              <Textarea
                label=""
                placeholder="Acciones recomendadas para mantener o mejorar el cumplimiento..."
                rows={3}
                value={compromisos}
                onChange={(e) => setCompromisos(e.target.value)}
              />
            </div>
            {!hasDireccion && (
              <div>
                <p className="text-[11px] font-medium mb-1.5" style={{ color: '#999' }}>
                  DIRECCIÓN
                </p>
                <Textarea
                  label=""
                  placeholder="Dirección completa de la empresa..."
                  rows={2}
                  value={direccion || ''}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </div>
            )}
            <p className="text-[11px]" style={{ color: '#999' }}>
              Los campos marcados con <span style={{ color: '#c0392b' }}>*</span> son obligatorios.
            </p>
          </div>
        </div>

        {/* Checklist de requisitos para enviar a revisión */}
        <div className="admin-card mt-6">
          <div className="admin-card-header">
            <span className="text-[13px] font-semibold" style={{ color: '#1a1a1a' }}>
              Requisitos para enviar a revisión
            </span>
          </div>
          <div className="px-5 py-4 space-y-2.5">
            {checklist.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                {item.ok ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#2d7a4f' }} />
                ) : (
                  <Circle className="w-4 h-4 shrink-0" style={{ color: '#c0392b' }} />
                )}
                <span className="text-[13px]" style={{ color: item.ok ? '#2d7a4f' : '#c0392b' }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end items-center gap-3 mt-6">
          {showSecretPanel && (
            <button
              type="button"
              onClick={handleSimularFinalizar}
              disabled={isSubmitting || simulacionModal.isSimulando}
              className="admin-btn font-bold flex items-center gap-1.5 transition-all cursor-pointer"
              style={{
                background: '#fef3c7',
                color: '#92400e',
                borderColor: '#fcd34d',
              }}
              title="Modo Debug (Ctrl+. activo por impersonación): Simula la respuesta sin modificar la base de datos"
            >
              {simulacionModal.isSimulando ? 'Simulando...' : '🧪 Simular reenvío a revisión'}
            </button>
          )}
          <button onClick={() => navigate(-1)} disabled={isSubmitting} className="admin-btn">
            Cancelar
          </button>
          <button
            onClick={handleFinalizar}
            disabled={isSubmitting || isCheckingAudioStatus}
            className="admin-btn primary"
            title={motivoBloqueo}
          >
            {isSubmitting ? 'Enviando...' : isCheckingAudioStatus ? 'Validando...' : 'Enviar a Verificación'}
          </button>
        </div>

        {/* Modal — Simulación de Envío a Revisión (Modo Debug) */}
        <Modal
          isOpen={simulacionModal.isOpen}
          onClose={() => setSimulacionModal((prev) => ({ ...prev, isOpen: false }))}
          title="🧪 Simulación de envío a revisión (Modo Debug)"
          size="md"
        >
          {simulacionModal.isSimulando ? (
            <div className="py-8 text-center text-gray-500 text-sm">Ejecutando simulación de reenvío...</div>
          ) : simulacionModal.resultado ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-900 font-semibold flex items-center gap-2">
                <span>⚠️</span>
                <span>MODO DEBUG (Ctrl+.) — Esta acción es una SIMULACIÓN. Ningún cambio ha sido guardado.</span>
              </div>

              {simulacionModal.resultado.ok ? (
                <div className="flex items-start gap-3 rounded-lg p-4 bg-emerald-50 border border-emerald-200">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-700 mt-0.5" />
                  <div>
                    <p className="font-bold text-[13px] text-emerald-900">Simulación Exitosa</p>
                    <p className="text-[12px] text-emerald-800 mt-1">{simulacionModal.resultado.message}</p>
                    {simulacionModal.resultado.formUrl && (
                      <p className="text-[11px] font-mono text-emerald-700 mt-2 break-all bg-emerald-100/50 p-2 rounded">
                        Formulario Audio: {simulacionModal.resultado.formUrl}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-lg p-4 bg-rose-50 border border-rose-200">
                  <AlertTriangle className="w-5 h-5 shrink-0 text-rose-600 mt-0.5" />
                  <div>
                    <p className="font-bold text-[13px] text-rose-900">Simulación: Envío Bloqueado</p>
                    <p className="text-[12px] text-rose-800 mt-1">{simulacionModal.resultado.message}</p>
                    {simulacionModal.resultado.items && simulacionModal.resultado.items.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {simulacionModal.resultado.items.map((item, idx) => {
                          const isObj = typeof item === 'object' && item !== null && 'text' in item;
                          const text = isObj ? (item as any).text : String(item);
                          return (
                            <li key={idx} className="flex items-start gap-1.5 text-[12px] text-rose-700 font-medium">
                              <span className="shrink-0">•</span>
                              <span>{text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Respuesta del Servidor (Payload Backend)
                </p>
                <pre className="text-[11px] font-mono bg-gray-900 text-emerald-400 p-3 rounded-lg overflow-x-auto max-h-48">
                  {JSON.stringify(simulacionModal.resultado, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
          <div className="flex justify-end gap-3 mt-5">
            <button className="admin-btn primary" onClick={() => setSimulacionModal((prev) => ({ ...prev, isOpen: false }))}>
              Cerrar Simulación
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
